/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { memo } from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import classNames from 'classnames';
import isEqual from 'lodash.isequal';

import { AppState } from 'services/types';
import { updateQueryString } from 'services/router/actions';
import {
    makeSelectVirtualizedTableColumnType,
    selectVirtualizedTableColumnLabel,
    makeSelectVirtualizedTableColumnLabel,
    selectVirtualizedTableColumnWidthAdjusted,
    makeSelectVirtualizedTableColumnWidthAdjusted,
    makeSelectVirtualizedTableColumnDisableSorting
} from 'services/virtualizedTable/selectors';
import { VirtualizedTableColumnKey } from 'services/virtualizedTable/types';

import VirtualizedTableHeaderColumnResizeHandle from '../VirtualizedTableHeaderColumnResizeHandle';
import {
    selectIsSorted,
    makeSelectIsSorted,
    selectIsAscending,
    makeSelectIsAscending,
    selectIsDescending,
    makeSelectIsDescending
} from './selectors';
import {
    FIELD_TYPE_LIST,
    ROW_HEIGHT,
    ASCENDING,
    DESCENDING
} from '../../constants';
import './styles.scss';

export interface OwnProps {
    columnKey: VirtualizedTableColumnKey;
}

export interface ConnectedProps {
    columnType?: ReturnType<
        ReturnType<typeof makeSelectVirtualizedTableColumnType>
    >;
    columnLabel?: ReturnType<typeof selectVirtualizedTableColumnLabel>;
    columnWidth?: ReturnType<typeof selectVirtualizedTableColumnWidthAdjusted>;
    isSorted?: ReturnType<typeof selectIsSorted>;
    isAscending?: ReturnType<typeof selectIsAscending>;
    isDescending?: ReturnType<typeof selectIsDescending>;
    disableSorting?: ReturnType<
        ReturnType<typeof makeSelectVirtualizedTableColumnDisableSorting>
    >;
}

export interface DispatchProps {
    updateQueryString: typeof updateQueryString;
}

export type Props = OwnProps & ConnectedProps & DispatchProps;

export const VirtualizedTableHeaderColumnComponent = (
    props: Props
): React.ReactElement => {
    const {
        columnKey,
        columnType,
        columnLabel,
        columnWidth,
        isSorted,
        isAscending,
        isDescending,
        disableSorting
    } = props;
    const [minWidth, setMinWidth] = React.useState(0);

    const handleClick = (): void | undefined => {
        if (disableSorting) return;
        const nextSortDirection = isAscending ? DESCENDING : ASCENDING;

        props.updateQueryString({
            sort: `${columnKey}_${nextSortDirection}`
        });
    };

    return (
        <div
            style={{
                width: columnWidth,
                position: 'relative'
            }}
        >
            <div
                className={classNames('virtualized-table-header-cell', {
                    disabled: disableSorting,
                    sorted: isSorted,
                    ascending: isAscending,
                    descending: isDescending && !isAscending
                })}
                style={{
                    height: ROW_HEIGHT
                }}
                onClick={handleClick}
            >
                {columnType === FIELD_TYPE_LIST && '# '}
                <span
                    ref={el => {
                        if (!el) return;
                        if (minWidth) return;
                        setMinWidth(el.getBoundingClientRect().width);
                    }}>{columnLabel}
                </span>
            </div>

            <VirtualizedTableHeaderColumnResizeHandle columnKey={columnKey} minWidth={minWidth} />
        </div>
    );
};

const mapStateToProps = createStructuredSelector<
    AppState,
    OwnProps,
    ConnectedProps
>({
    columnType: makeSelectVirtualizedTableColumnType(),
    columnLabel: makeSelectVirtualizedTableColumnLabel(),
    columnWidth: makeSelectVirtualizedTableColumnWidthAdjusted(),
    isSorted: makeSelectIsSorted(),
    isAscending: makeSelectIsAscending(),
    isDescending: makeSelectIsDescending(),
    disableSorting: makeSelectVirtualizedTableColumnDisableSorting()
});

export default connect(mapStateToProps, {
    updateQueryString
})(memo(VirtualizedTableHeaderColumnComponent, isEqual));
